import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo, { SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { rankBoards } from "@/mocks/rank";
import { formatHeat } from "@/utils/format";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const list = articles.filter((item) => item.categorySlug === slug);
  const hotList = rankBoards.realtime.slice(0, 5);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category ? `${category.name}话题整理` : "话题分类",
      url: `${SITE_ORIGIN}/category/${slug}`,
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: category?.name ?? "分类", item: `${SITE_ORIGIN}/category/${slug}` },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${SITE_ORIGIN}/article/${item.id}`,
        })),
      },
    }),
    [category, slug, list]
  );

  useSeo({
    title: category ? `${category.name}话题整理 | 网爆吃瓜实时楼与现场直播` : "话题分类 | 吃瓜前线",
    description: category
      ? `${category.desc} 本页整理「${category.name}」方向的网爆吃瓜实时楼、现场直播与秒删截图话题，持续按时间线更新。`
      : "吃瓜前线话题分类页，按方向整理网爆吃瓜现场直播、实时楼与秒删截图相关内容。",
    keywords: category
      ? `${category.name},网爆吃瓜,网爆吃瓜实时楼,网爆吃瓜现场直播`
      : "网爆吃瓜,网爆吃瓜实时楼,话题分类",
    path: `/category/${slug}`,
    jsonLd,
  });

  if (!category) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto max-w-lg rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950">没有找到这个分类</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已经被合并或改名了。</p>
          <Link
            to="/"
            className="mt-5 inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: category.name }]} />

      <header className="border-b border-background-200 pb-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
          <i className={category.icon} aria-hidden="true"></i>
          话题分类
        </span>
        <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
          {category.name}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
        <p className="mt-3 font-label text-xs text-foreground-500">本分类已收录 {category.count} 篇话题整理</p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {list.length === 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
              <p className="font-label text-sm text-foreground-700">这个分类暂时还没有话题，编辑组正在整理。</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} variant="wide" />
              ))}
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-4">
          <section className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">实时热度前五</h2>
            <ol className="mt-4 flex flex-col gap-3">
              {hotList.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/article/${item.id}`}
                    className="group flex cursor-pointer items-start gap-3"
                  >
                    <span className="font-heading text-sm font-bold text-primary-500">{index + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 block font-label text-xs text-foreground-500">
                        {formatHeat(item.heat)} 热度
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">其他分类</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="cursor-pointer rounded-full border border-background-200 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}